import {
  cancel_chatbot_chat_message_path,
  chatbot_chat_messages_path,
  chatbot_chat_path,
  chatbot_chats_path,
  chatbot_organization_files_path,
  pending_chatbot_chat_messages_path
} from "@/routes";

export const SUPPORTED_FILE_ACCEPT = [
  ".asm", ".bat", ".c", ".cc", ".conf", ".cpp", ".cs", ".css", ".csv", ".cxx", ".def", ".dic", ".doc", ".docx", ".dot",
  ".eml", ".h", ".hh", ".htm", ".html", ".ics", ".ifb", ".iif", ".in", ".java", ".js", ".json", ".ksh", ".list",
  ".log", ".markdown", ".md", ".mht", ".mhtml", ".mime", ".mjs", ".odt", ".pdf", ".php", ".pl", ".pot", ".ppa",
  ".pps", ".ppt", ".pptx", ".pwz", ".py", ".rb", ".rst", ".rtf", ".s", ".sh", ".sql", ".srt", ".tex", ".text",
  ".ts", ".tsv", ".txt", ".vcf", ".vtt", ".wiz", ".xla", ".xlb", ".xlc", ".xlm", ".xls", ".xlsx", ".xlt", ".xlw",
  ".xml"
].join(",");

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

async function parseResponse(response) {
  const body = await response.text();
  let payload = {};
  try {
    payload = body ? JSON.parse(body) : {};
  } catch (_parseError) {
    payload = {};
  }
  if (!response.ok) {
    const error = new Error(payload.error || `Forespørgslen fejlede (${response.status}). Prøv igen om lidt.`);
    error.payload = payload;
    throw error;
  }

  return payload;
}

export async function createChatbotChat(title) {
  const response = await fetch(chatbot_chats_path({ format: "json" }), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken()
    },
    credentials: "same-origin",
    body: JSON.stringify({ chat: { title } })
  });

  return (await parseResponse(response)).chat;
}

export function toOptimisticFile(file, index = 0) {
  if (typeof File !== "undefined" && file instanceof File) {
    return {
      id: `upload-${index}-${file.name}`,
      source: "upload",
      filename: file.name,
      name: file.name,
      size: file.size,
      byte_size: file.size,
      fileObject: file
    };
  }

  return { ...file, source: "organization_file" };
}

export async function sendChatbotMessage(chatId, { question, uploads, organizationFiles, clientRequestId, reasoningEffort, mode }) {
  const body = new FormData();
  body.append("question", question);
  if (clientRequestId) body.append("client_request_id", clientRequestId);
  if (reasoningEffort) body.append("reasoning_effort", reasoningEffort);
  if (mode) body.append("mode", mode);
  uploads.forEach((file) => body.append("attachments[]", file));
  organizationFiles.forEach((file) => body.append("organization_file_ids[]", file.id));

  const response = await fetch(chatbot_chat_messages_path(chatId, { format: "json" }), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken()
    },
    credentials: "same-origin",
    body
  });

  return parseResponse(response);
}

// Used by the widget to restore a conversation it only remembers by id.
// A deleted or no longer visible chat is an expected missing conversation.
export async function fetchChatbotChat(chatId) {
  const response = await fetch(chatbot_chat_path(chatId, { format: "json" }), {
    headers: { Accept: "application/json" },
    credentials: "same-origin"
  });

  if (response.status === 404) return null;

  return (await parseResponse(response)).chat || null;
}

export async function fetchChatbotChats({ signal } = {}) {
  const response = await fetch(chatbot_chats_path({ format: "json" }), {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    signal
  });

  const payload = await parseResponse(response);
  if (!Array.isArray(payload.chats)) throw new Error("Chatoversigten kunne ikke opdateres.");

  return payload.chats;
}

// The answer is generated by a background job, so the send only gets an
// acknowledgement. This is how both surfaces find out how it went.
export async function fetchChatbotPendingMessage(chatId, clientRequestId) {
  const response = await fetch(
    pending_chatbot_chat_messages_path(chatId, {
      format: "json",
      ...(clientRequestId ? { client_request_id: clientRequestId } : {})
    }),
    {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    }
  );

  return parseResponse(response);
}

export async function cancelChatbotMessage(chatId, messageId) {
  const response = await fetch(cancel_chatbot_chat_message_path(chatId, messageId, { format: "json" }), {
    method: "POST",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken()
    },
    credentials: "same-origin"
  });

  return parseResponse(response);
}

export async function loadChatbotOrganizationFiles() {
  const response = await fetch(chatbot_organization_files_path({ format: "json" }), {
    headers: { Accept: "application/json" },
    credentials: "same-origin"
  });
  const payload = await parseResponse(response);
  return payload.organization_files || [];
}
